import { TodoIntegrationType } from '@demind-inc/core';
import { debounce } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { tasksApi } from '../api';
import { FirestoreTaskItem } from '../types';
import {
  SyncProviderRequest,
  SyncProviderTasksParams,
  useProviderTasksMutation,
} from './useSyncProviderTasksBase';

export type SupportedTodoProvider = Extract<
  TodoIntegrationType,
  'todoist' | 'github' | 'ticktick' | 'trello' | 'notion' | 'clickup' | 'google-tasks'
>;
const SUPPORTED_TODO_PROVIDERS = [
  'todoist',
  'github',
  'ticktick',
  'trello',
  'notion',
  'clickup',
  'google-tasks',
] as const;

const todoistRequest: SyncProviderRequest = async ({ userId, todoTasksBoardIds, fullSync }) => {
  const { data } = await tasksApi.syncTodoistTasksV3({
    userId,
    todoTasksBoardIds,
    fullSync,
  });
  return (data?.updatedTasks as FirestoreTaskItem[]) ?? [];
};

const githubRequest: SyncProviderRequest = async ({ userId, todoTasksBoardIds, fullSync }) => {
  const { data } = await tasksApi.syncGitHubTasks({
    userId,
    todoTasksBoardIds,
    fullSync,
  });
  return (data?.updatedTasks as FirestoreTaskItem[]) ?? [];
};

const ticktickRequest: SyncProviderRequest = async ({ userId, todoTasksBoardIds, fullSync }) => {
  const { data } = await tasksApi.syncTickTickTasks({
    userId,
    todoTasksBoardIds,
    fullSync,
  });
  return (data?.updatedTasks as FirestoreTaskItem[]) ?? [];
};

const trelloRequest: SyncProviderRequest = async ({ userId, todoTasksBoardIds, fullSync }) => {
  const { data } = await tasksApi.syncTrelloTasks({
    userId,
    todoTasksBoardIds,
    fullSync,
  });
  return (data?.updatedTasks as FirestoreTaskItem[]) ?? [];
};

const notionRequest: SyncProviderRequest = async ({ userId, todoTasksBoardIds, fullSync }) => {
  const { data } = await tasksApi.syncNotionTasks({
    userId,
    todoTasksBoardIds,
    fullSync,
  });
  return (data?.updatedTasks as FirestoreTaskItem[]) ?? [];
};

const clickupRequest: SyncProviderRequest = async ({ userId, todoTasksBoardIds, fullSync }) => {
  const { data } = await tasksApi.syncClickUpTasks({
    userId,
    todoTasksBoardIds,
    fullSync,
  });
  return (data?.updatedTasks as FirestoreTaskItem[]) ?? [];
};

const googleTasksRequest: SyncProviderRequest = async ({ userId, todoTasksBoardIds, fullSync }) => {
  const { data } = await tasksApi.syncGoogleTasks({
    userId,
    todoTasksBoardIds,
    fullSync,
  });
  return (data?.updatedTasks as FirestoreTaskItem[]) ?? [];
};

const requestByProvider: Record<SupportedTodoProvider, SyncProviderRequest> = {
  todoist: todoistRequest,
  github: githubRequest,
  ticktick: ticktickRequest,
  trello: trelloRequest,
  notion: notionRequest,
  clickup: clickupRequest,
  'google-tasks': googleTasksRequest,
};

export const isSupportedTodoProvider = (
  source: TodoIntegrationType
): source is SupportedTodoProvider => {
  return SUPPORTED_TODO_PROVIDERS.includes(source as SupportedTodoProvider);
};

export const useSyncTodoProviders = () => {
  const queryClient = useQueryClient();

  const debouncedInvalidate = useMemo(
    () =>
      debounce(() => {
        queryClient.invalidateQueries({ queryKey: ['lifestack.todo.tasks'] });
        queryClient.invalidateQueries({ queryKey: ['lifestack.todo.overdueTasks'] });
      }, 1000),
    [queryClient]
  );

  useEffect(() => {
    return () => {
      debouncedInvalidate.cancel();
    };
  }, [debouncedInvalidate]);

  const todoistMutation = useProviderTasksMutation('todoist', requestByProvider.todoist, {
    onInvalidate: debouncedInvalidate,
  });
  const githubMutation = useProviderTasksMutation('github', requestByProvider.github, {
    onInvalidate: debouncedInvalidate,
  });
  const ticktickMutation = useProviderTasksMutation('ticktick', requestByProvider.ticktick, {
    onInvalidate: debouncedInvalidate,
  });
  const trelloMutation = useProviderTasksMutation('trello', requestByProvider.trello, {
    onInvalidate: debouncedInvalidate,
  });
  const notionMutation = useProviderTasksMutation('notion', requestByProvider.notion, {
    onInvalidate: debouncedInvalidate,
  });
  const clickupMutation = useProviderTasksMutation('clickup', requestByProvider.clickup, {
    onInvalidate: debouncedInvalidate,
  });
  const googleTasksMutation = useProviderTasksMutation(
    'google-tasks',
    requestByProvider['google-tasks'],
    {
      onInvalidate: debouncedInvalidate,
    }
  );

  const mutations: Record<SupportedTodoProvider, ReturnType<typeof useProviderTasksMutation>> = {
    todoist: todoistMutation,
    github: githubMutation,
    ticktick: ticktickMutation,
    trello: trelloMutation,
    notion: notionMutation,
    clickup: clickupMutation,
    'google-tasks': googleTasksMutation,
  };

  const syncProviderTasks = (provider: SupportedTodoProvider, params: SyncProviderTasksParams) => {
    return mutations[provider]?.mutateAsync(params);
  };

  const isPending = Object.values(mutations).some((mutation) => mutation.isPending);

  return {
    syncProviderTasks,
    mutations,
    isPending,
  };
};
